import { Editor, Extension } from '@tiptap/core'
import { PluginKey } from '@tiptap/pm/state'
import { ReactRenderer } from '@tiptap/react'
import Suggestion, {
  SuggestionProps,
  SuggestionKeyDownProps,
} from '@tiptap/suggestion'
import tippy from 'tippy.js'
import { MenuList } from './MenuList'
import { GROUPS } from './groups'
import type { Group } from './types'

const extensionName = 'slashCommand'

let popup: any

export interface SlashCommandOptions {
  /**
   * Override the command groups shown in the slash-command menu.
   * Callers like the task description editor pass a filtered subset
   * (headings + lists + quote + code) to avoid exposing full-doc
   * features like tables / media / admonitions in compact surfaces.
   */
  groups?: Group[]
}

export const SlashCommand = Extension.create<SlashCommandOptions>({
  name: extensionName,

  priority: 200,

  addOptions() {
    return {
      groups: undefined,
    }
  },

  onCreate() {
    popup = tippy('body', {
      interactive: true,
      trigger: 'manual',
      placement: 'bottom-start',
      theme: 'slash-command',
      maxWidth: '16rem',
      offset: [16, 8],
      popperOptions: {
        strategy: 'fixed',
        modifiers: [
          {
            name: 'flip',
            enabled: false,
          },
        ],
      },
    })
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        char: '/',
        allowSpaces: true,
        startOfLine: false, // Allow slash command anywhere
        pluginKey: new PluginKey(extensionName),
        allow: ({ state, range }) => {
          const $from = state.doc.resolve(range.from)
          const isRootDepth = $from.depth === 1
          const isParagraph = $from.parent.type.name === 'paragraph'

          // Check if we're in a list item (bullet/ordered/task list)
          const isInListItem = this.editor.isActive('listItem') || this.editor.isActive('taskItem')

          // Check if this is at the start of a block (for block commands)
          const isStartOfNode = $from.parent.textContent?.charAt(0) === '/'

          // Check if this is inline (for inline commands)
          const parentStart = $from.start()
          const posInParent = range.from - parentStart
          const textBefore = $from.parent.textContent?.substring(0, posInParent)
          const isInline =
            !!textBefore && textBefore.trim().length > 0 && !isStartOfNode

          // Store whether this is inline mode in editor storage
          // In list items, always treat as inline mode (show only inline commands)
          this.editor.storage[extensionName].isInlineMode = isInListItem || isInline

          // TODO
          const isInColumn = this.editor.isActive('column')
          const isInCard = this.editor.isActive('Card')
          const isInAccordion = this.editor.isActive('Accordion')
          const isInStep = this.editor.isActive('Step')
          const isInTabs = this.editor.isActive('Tabs')
          const isInTab = this.editor.isActive('Tab')
          const isInAdmonition = this.editor.isActive('Admonition')
          // const isCodeBlock = this.editor.isActive('codeBlock')
          // const isImage = this.editor.isActive('image')

          const afterContent = $from.parent.textContent?.substring(
            $from.parent.textContent?.indexOf('/')
          )
          const isValidAfterContent = !afterContent?.endsWith('  ')

          // Allow block commands at start of node (but NOT in list items)
          const allowBlockCommands =
            ((isRootDepth && isParagraph && isStartOfNode) ||
              (isInColumn && isParagraph && isStartOfNode) ||
              (isInCard && isParagraph && isStartOfNode) ||
              (isInAccordion && isParagraph && isStartOfNode) ||
              (isInStep && isParagraph && isStartOfNode) ||
              (isInTabs && isParagraph && isStartOfNode) ||
              (isInTab && isParagraph && isStartOfNode) ||
              (isInAdmonition && isParagraph && isStartOfNode)) &&
            isValidAfterContent

          // Allow inline commands within text (including in list items)
          // Also allow at start of list items for inline-only commands
          const allowInlineCommands =
            ((isInline && isParagraph) || isInListItem) && isValidAfterContent

          return allowBlockCommands || allowInlineCommands
        },
        command: ({
          editor,
          range,
          props,
        }: {
          editor: Editor
          range: any
          props: any
        }) => {
          // Check if the command handles deletion itself
          if (!props.handlesDeletion) {
            // Delete the entire suggestion range (includes / and the typed query)
            editor.chain().focus().deleteRange(range).run()
          }

          // Pass the range to the action so commands can know where insertion should happen
          props.action(editor, range)
          popup?.[0].hide()
        },
        items: ({ query }: { query: string }) => {
          const isInlineMode = this.editor.storage[extensionName].isInlineMode
          const activeGroups = this.options.groups ?? GROUPS

          const withFilteredCommands = activeGroups.map((group) => ({
            ...group,
            commands: group.commands
              .filter((item) => {
                // Filter based on inline mode
                if (isInlineMode) {
                  // Only show inline-compatible commands
                  return item.isInlineCommand === true
                } else {
                  // Show block commands (including inline-only commands)
                  return true
                }
              })
              .filter((item) => {
                const labelNormalized = item.label.toLowerCase().trim()
                const queryNormalized = query.toLowerCase().trim()

                if (item.aliases) {
                  const aliases = item.aliases.map((alias) =>
                    alias.toLowerCase().trim()
                  )

                  return (
                    labelNormalized.includes(queryNormalized) ||
                    aliases.includes(queryNormalized)
                  )
                }

                return labelNormalized.includes(queryNormalized)
              })
              .filter((command) =>
                command.shouldBeHidden
                  ? !command.shouldBeHidden(this.editor)
                  : true
              ),
          }))

          const withoutEmptyGroups = withFilteredCommands.filter((group) => {
            if (group.commands.length > 0) {
              return true
            }

            return false
          })

          const withEnabledSettings = withoutEmptyGroups.map((group) => ({
            ...group,
            commands: group.commands.map((command) => ({
              ...command,
              isEnabled: true,
            })),
          }))

          return withEnabledSettings
        },
        render: () => {
          let component: any

          let scrollHandler: (() => void) | null = null

          return {
            onStart: (props: SuggestionProps) => {
              component = new ReactRenderer(MenuList, {
                props,
                editor: props.editor,
              })

              const { view } = props.editor

              const getReferenceClientRect = () => {
                if (!props.clientRect) {
                  return props.editor.storage[extensionName].rect
                }

                const rect = props.clientRect()

                if (!rect) {
                  return props.editor.storage[extensionName].rect
                }

                let yPos = rect.y

                if (
                  rect.top + component.element.offsetHeight + 40 >
                  window.innerHeight
                ) {
                  const diff =
                    rect.top +
                    component.element.offsetHeight -
                    window.innerHeight +
                    40
                  yPos = rect.y - diff
                }

                return new DOMRect(rect.x, yPos, rect.width, rect.height)
              }

              scrollHandler = () => {
                popup?.[0].setProps({
                  getReferenceClientRect,
                })
              }

              view.dom.parentElement?.addEventListener('scroll', scrollHandler)

              popup?.[0].setProps({
                getReferenceClientRect,
                appendTo: () => document.body,
                content: component.element,
              })

              popup?.[0].show()
            },

            onUpdate(props: SuggestionProps) {
              component.updateProps(props)

              const { view } = props.editor

              const getReferenceClientRect = () => {
                if (!props.clientRect) {
                  return props.editor.storage[extensionName].rect
                }

                const rect = props.clientRect()

                if (!rect) {
                  return props.editor.storage[extensionName].rect
                }

                // Account for when the editor is bound inside a container that doesn't go all the way to the edge of the screen
                return new DOMRect(rect.x, rect.y, rect.width, rect.height)
              }

              const scrollHandler = () => {
                popup?.[0].setProps({
                  getReferenceClientRect,
                })
              }

              view.dom.parentElement?.addEventListener('scroll', scrollHandler)

              // eslint-disable-next-line no-param-reassign
              props.editor.storage[extensionName].rect = props.clientRect
                ? getReferenceClientRect()
                : {
                    width: 0,
                    height: 0,
                    left: 0,
                    top: 0,
                    right: 0,
                    bottom: 0,
                  }
              popup?.[0].setProps({
                getReferenceClientRect,
              })
            },

            onKeyDown(props: SuggestionKeyDownProps) {
              if (props.event.key === 'Escape') {
                popup?.[0].hide()

                return true
              }

              if (!popup?.[0].state.isShown) {
                popup?.[0].show()
              }

              return component.ref?.onKeyDown(props)
            },

            onExit(props) {
              popup?.[0].hide()
              if (scrollHandler) {
                const { view } = props.editor
                view.dom.parentElement?.removeEventListener(
                  'scroll',
                  scrollHandler
                )
              }
              component?.destroy()
            },
          }
        },
      }),
    ]
  },

  addStorage() {
    return {
      rect: {
        width: 0,
        height: 0,
        left: 0,
        top: 0,
        right: 0,
        bottom: 0,
      },
      isInlineMode: false,
    }
  },
})

export default SlashCommand
